import { useState } from "react";
import { useScrollLock } from "shared/lib/hooks";
import { SignInForm } from "..";
import { RegistrationIndividualForm } from "..";
import { RegistrationBusinessForm } from "..";
import { EmailConfirmForm } from "..";
import { LoginTypeSwitcher } from "..";
import { RegistrationTypeSwitcher } from "..";
import { EmailEnterForm } from "..";
import { PasswordEnterForm } from "./password-reset/PasswordEnterForm";
import { verifyOtpRequest } from "../api/verifyOtpRequest";
import { verifyResetCodeRequest } from "../api/verifyResetCodeRequest";
import { updateVerifyCodeRequest } from "../api/updateVerifyCodeRequest";
import { forgotPasswordRequest } from "../api/forgotPasswordRequest";
import styles from "./LoginSidebar.module.scss";

const FORM_STATES = {
  SIGN_IN: "sign-in",
  REGISTRATION_INDIVIDUAL: "registration-individual",
  REGISTRATION_BUSINESS: "registration-business",
  EMAIL_CONFIRM: "email-confirm",
  EMAIL_ENTER: "email-enter",
  PASSWORD_RESET: "password-reset",
  PASSWORD_ENTER: "password-enter",
};

export const LoginSidebar = ({ isCollapsed, open, close, toggle, variant }) => {
  useScrollLock(!isCollapsed);

  const [currentForm, setCurrentForm] = useState(FORM_STATES.SIGN_IN);
  const [userEmail, setUserEmail] = useState("");

  const [resetToken, setResetToken] = useState("");

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      close?.();
    }
  };

  const handleCloseSidebar = () => {
    close?.();
    setCurrentForm(FORM_STATES.SIGN_IN);
  };

  const handleRegistrationSuccess = (email) => {
    setUserEmail(email);
    setCurrentForm(FORM_STATES.EMAIL_CONFIRM);
  };

  const handleForgotPassword = () => {
    setUserEmail("");
    setCurrentForm(FORM_STATES.EMAIL_ENTER);
  };

  const handlePasswordReset = (email) => {
    setUserEmail(email);
    setCurrentForm(FORM_STATES.PASSWORD_RESET);
  };

  const handleShowSignInForm = () => {
    setCurrentForm(FORM_STATES.SIGN_IN);
  };

  const handlePasswordResetSuccess = () => {
    setCurrentForm(FORM_STATES.PASSWORD_ENTER);
  };

  const resendVerifyCode = () => updateVerifyCodeRequest({ email: userEmail });
  const resendResetCode = () => forgotPasswordRequest({ email: userEmail });

  const shouldShowLoginTypeSwitcher = [
    FORM_STATES.SIGN_IN,
    FORM_STATES.REGISTRATION_INDIVIDUAL,
    FORM_STATES.REGISTRATION_BUSINESS,
  ].includes(currentForm);

  const shouldShowRegistrationSwitcher = [
    FORM_STATES.REGISTRATION_INDIVIDUAL,
    FORM_STATES.REGISTRATION_BUSINESS,
  ].includes(currentForm);

  const handleLoginTypeChange = (tab) => {
    if (tab === "sign-in") {
      setCurrentForm(FORM_STATES.SIGN_IN);
    } else if (tab === "registration") {
      setCurrentForm(FORM_STATES.REGISTRATION_INDIVIDUAL);
    }
  };

  const handleRegistrationTypeChange = (type) => {
    if (type === "individual") {
      setCurrentForm(FORM_STATES.REGISTRATION_INDIVIDUAL);
    } else if (type === "business") {
      setCurrentForm(FORM_STATES.REGISTRATION_BUSINESS);
    }
  };

  const renderForm = () => {
    switch (currentForm) {
      //авторизация
      case FORM_STATES.SIGN_IN:
        return (
          <SignInForm
            onForgotPassword={handleForgotPassword}
            onSuccess={handleCloseSidebar}
          />
        );
      //регистрация физ. лицо
      case FORM_STATES.REGISTRATION_INDIVIDUAL:
        return (
          <RegistrationIndividualForm onSuccess={handleRegistrationSuccess} />
        );
      //регистрация юр. лицо
      case FORM_STATES.REGISTRATION_BUSINESS:
        return (
          <RegistrationBusinessForm onSuccess={handleRegistrationSuccess} />
        );
      //регистрация: ввод кода подверждения почты
      case FORM_STATES.EMAIL_CONFIRM:
        return (
          <EmailConfirmForm
            email={userEmail}
            title="Подтверждение почты"
            onSuccess={handleShowSignInForm}
            requestFn={verifyOtpRequest}
            onResendCode={resendVerifyCode}
          />
        );
      //сброс пароля: ввод почты
      case FORM_STATES.EMAIL_ENTER:
        return (
          <EmailEnterForm
            email={userEmail}
            onSuccess={handleForgotPassword}
            onPasswordReset={handlePasswordReset}
          />
        );
      //сброс пароля: ввод кода подверждения
      case FORM_STATES.PASSWORD_RESET:
        return (
          <EmailConfirmForm
            email={userEmail}
            title="Сброс пароля"
            onSuccess={handlePasswordResetSuccess}
            requestFn={verifyResetCodeRequest}
            onTokenReceived={setResetToken}
            onResendCode={resendResetCode}
          />
        );
      //сброс пароля: ввод нового пароля
      case FORM_STATES.PASSWORD_ENTER:
        return (
          <PasswordEnterForm
            email={userEmail}
            onSuccess={handleShowSignInForm}
            resetToken={resetToken}
          />
        );

      default:
        return null;
    }
  };

  if (isCollapsed) return null;

  return (
    <div
      className={`${styles.sidebarOverlay} ${styles[variant]}`}
      onClick={handleOverlayClick}
    >
      <div className={`${styles.sidebarContainer} ${styles[variant]}`}>
        <div className={styles.sidebarWrapper}>
          {shouldShowLoginTypeSwitcher && (
            <LoginTypeSwitcher
              activeTab={
                currentForm === FORM_STATES.SIGN_IN ? "sign-in" : "registration"
              }
              onTabChange={handleLoginTypeChange}
            />
          )}

          {shouldShowRegistrationSwitcher && (
            <RegistrationTypeSwitcher
              registrationType={
                currentForm === FORM_STATES.REGISTRATION_INDIVIDUAL
                  ? "individual"
                  : "business"
              }
              onTypeChange={handleRegistrationTypeChange}
            />
          )}

          {renderForm()}
        </div>
      </div>
    </div>
  );
};
